import { BREAKPOINTS, DSFlex, DSSpacer, DSSurface, DSSurfaceColorType, DSText } from '@zoe/ds-web'
import React from 'react'
import styled from 'styled-components'

import { FragmentComponent } from '@/types/graphql'

import { DSGridBlock } from '@/components/ds/Grid/Grid'

import { responsiveImageBlock } from '@/lib/api/graphql/utils/commonBlocks'
import { ctaCollectionBlock } from '@/lib/api/graphql/utils/commonBlocks'

import { processTypographyMarkdown } from '../DesignSystem/Typography'
import { CtaCollectionBlock, CtaCollectionRecord } from './CtaCollectionBlock'

export type ContentAndImageBlockContent = {
  eyebrow?: string
  title?: string
  description?: string
  ctaCollection?: CtaCollectionRecord
  textVariant?: 'alt' | 'default'
  dataTestId?: string
}

export type ContentAndImageBlockRecord = {
  __typename: 'ContentAndImageBlockRecord'
  id: string
  image: {
    responsiveImage: {
      alt: string
      aspectRatio: number
      base64: string
      bgColor: string
      height: number
      sizes: string
      src: string
      srcSet: string
      title: string
      webpSrcSet: string
      width: number
    }
  }
  content: ContentAndImageBlockContent
  imageOnTheLeft?: boolean
  isImageCover?: boolean
  backgroundColour?: DSSurfaceColorType
  dataTestId?: string
}

const fragment = `
  fragment ContentAndImageBlockFragment on ContentAndImageSectionRecord {
    __typename
    id
    image {
      ${responsiveImageBlock()}
    }
    content {
      eyebrow
      title
      description
      ${ctaCollectionBlock}
      textVariant
      }
    imageOnTheLeft
    isImageCover
    backgroundColour
    dataTestId
  }
`

const ImageBlock = ({ record }: { record: ContentAndImageBlockRecord }) => {
  return (
    <DSGridBlock
      padded
      breakConfig={{
        s: { colSpan: 2 },
        m: { colSpan: 3 },
        l: { colSpan: 6 },
        xl: { colSpan: 6 },
      }}
    >
      {record.isImageCover ? (
        <div
          style={{
            backgroundImage: `url(${record.image.responsiveImage.src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            aspectRatio: '1/1',
            width: '100%',
            height: '100%',
            display: 'block',
            minHeight: '350px',
          }}
        ></div>
      ) : (
        <>
          <DSSpacer direction="vertical" size={0} breakAt={{ m: 72 }} />
          <img
            src={record.image.responsiveImage.src}
            alt={record.image.responsiveImage.alt}
            style={{ width: '100%', height: 'auto', borderRadius: '32px' }}
          />
          <DSSpacer direction="vertical" size={0} breakAt={{ m: 48 }} />
        </>
      )}
    </DSGridBlock>
  )
}

const ContentBlock = ({ record }: { record: ContentAndImageBlockRecord }) => {
  return (
    <DSGridBlock
      padded
      breakConfig={{
        s: { colSpan: 2 },
        m: { colSpan: 3 },
        l: { colSpan: 5 },
        xl: { colSpan: 5 },
      }}
    >
      {record.isImageCover && (
        <>
          <DSSpacer direction="vertical" size={72} breakAt={{ m: 0 }} />
          <DSSpacer direction="vertical" size={144} breakAt={{ m: 0 }} />
        </>
      )}
      {record.content.eyebrow && (
        <DSFlex direction="column">
          <DSText
            as="h5"
            variant="fluid-eyebrow-100"
            weight="semiBold"
            align="left"
            breakAt={{ breakPoint: 'm', align: 'center' }}
          >
            {record.content.eyebrow}
          </DSText>
          <DSSpacer direction="vertical" size={16} breakAt={{ m: 8 }} />
        </DSFlex>
      )}
      {record.content.title && (
        <DSFlex direction="column">
          <DSText
            as="h1"
            variant={record.content.textVariant === 'alt' ? 'fluid-display-1000alt' : 'fluid-heading-900'}
            align="left"
            weight={record.content.textVariant === 'alt' ? 'semiBold' : 'book'}
            breakAt={{
              breakPoint: 'm',
              align: 'center',
              variant: record.content.textVariant === 'alt' ? 'fluid-heading-900alt' : 'fluid-heading-800',
            }}
          >
            {processTypographyMarkdown({
              content: record.content.title,
              variant: record.content.textVariant === 'alt' ? 'fluid-display-1000alt' : 'fluid-heading-900',
              weight: record.content.textVariant === 'alt' ? 'semiBold' : 'book',
              mobileVariant: record.content.textVariant === 'alt' ? 'fluid-heading-900alt' : 'fluid-heading-800',
            })}
          </DSText>
          <DSSpacer direction="vertical" size={24} />
        </DSFlex>
      )}
      {record.content.description && (
        <DSFlex direction="column">
          <DSText
            as="p"
            variant="fluid-paragraph-400"
            weight="light"
            align="left"
            breakAt={{ breakPoint: 'm', align: 'center', variant: 'fluid-paragraph-300' }}
          >
            {processTypographyMarkdown({
              content: record.content.description,
              variant: 'fluid-paragraph-400',
              weight: 'light',
              mobileVariant: 'fluid-paragraph-300',
            })}
          </DSText>
          <DSSpacer direction="vertical" size={24} />
        </DSFlex>
      )}
      {record.content.ctaCollection && (
        <DSFlex
          breakConfig={{
            maxViewportDirection: 'row',
            breakAt: {
              breakPoint: 'm',
              direction: 'column',
              alignItems: 'center',
            },
          }}
          alignItems="flex-start"
        >
          <CtaCollectionBlock record={record.content.ctaCollection} />
        </DSFlex>
      )}
      {record.isImageCover && (
        <>
          <DSSpacer direction="vertical" size={72} breakAt={{ m: 0 }} />
          <DSSpacer direction="vertical" size={144} breakAt={{ m: 0 }} />
        </>
      )}
      <DSSpacer direction="vertical" size={0} breakAt={{ m: 72 }} />
    </DSGridBlock>
  )
}

const StyledContentAndImageBlock = styled.div<{ $reverse: boolean; $isImageCover?: boolean }>`
  display: flex;
  flex-direction: ${({ $reverse }) => ($reverse ? 'row-reverse' : 'row')};
  max-width: 1640px;
  width: 100%;

  ${({ $isImageCover }) =>
    !$isImageCover &&
    `
    align-items: center;
  `}
  > * {
    flex: 1;
  }

  @media (max-width: ${BREAKPOINTS.m}) {
    flex-direction: column;
  }
`

export const ContentAndImageBlock: FragmentComponent<{}, ContentAndImageBlockRecord> = ({ record }) => {
  return (
    <DSSurface surfaceColor={record.backgroundColour}>
      <div style={{ margin: '0 auto' }}>
        <DSFlex direction="column" alignItems="center">
          {!record.isImageCover && <DSSpacer direction="vertical" size={144} breakAt={{ m: 0 }} />}
          <StyledContentAndImageBlock $reverse={!record.imageOnTheLeft} $isImageCover={record.isImageCover}>
            <ImageBlock record={record} />
            <ContentBlock record={record} />
          </StyledContentAndImageBlock>
          {!record.isImageCover && <DSSpacer direction="vertical" size={144} breakAt={{ m: 0 }} />}
        </DSFlex>
      </div>
    </DSSurface>
  )
}

ContentAndImageBlock.fragment = fragment
ContentAndImageBlock.recordName = 'ContentAndImageSectionRecord'
